import { colorsButton } from '__app/colors';
import Icon from '__components/Kizen/Icon';
import { FC, useMemo } from 'react';
import {
  StyledAdornmentContainer,
  StyledAdornments,
  StyledIconButtonWrapper,
  StyledOption,
  StyledOptionLabel,
} from './styles';
import { Button } from '@kizen/kds/Button';
import { Icon as KDSIcon } from '@kizen/kds/Icon';
import { ValidIcons } from '@kizen/kds/Icon';
import { DataAdornmentVariant } from './types';
import IconButtonAbsoluteMenu from '__components/Tables/Big/IconButtonAbsoluteMenu';
import { useTranslation } from 'react-i18next';
import { Tooltip, useTooltip } from '@kizen/kds/Tooltip';
import { DataAdornment } from 'ts-components/Plugins/types';

type ScriptArgs = Record<string, any>;

type AdornmentMenuProps = {
  adornments: DataAdornment[];
  disabled: boolean;
  onExecuteScript: (adornment: DataAdornment) => void;
};

export type IconDataAdornmentsProps = {
  adornments: DataAdornment[];
  scriptArgs: ScriptArgs;
  disabled: boolean;
  executeScript: (
    script: string,
    params: ScriptArgs,
    executionPlugin: DataAdornment
  ) => Promise<any>;
  variant?: DataAdornmentVariant;
  translateYValue?: string;
};

type AdornmentOption = {
  value?: string;
  label: string;
  adornment: DataAdornment;
};

const customStyles = {
  option: (provided: any) => ({
    ...provided,
    backgroundColor: 'transparent',
    padding: 0,
  }),
};

const validateSvg = (dataUrl?: string) => {
  return dataUrl && dataUrl.startsWith('data:image/');
};

const AdornmentsMenu: FC<AdornmentMenuProps> = ({
  adornments,
  disabled,
  onExecuteScript,
}) => {
  const { t } = useTranslation();
  const handleChooseValue = ({ adornment }: AdornmentOption) => {
    onExecuteScript(adornment);
  };

  const menuOptions = useMemo(() => {
    return adornments
      .map((adornment) => ({
        value: adornment.id ?? adornment.plugin_name,
        label: adornment.config?.tooltip || ' ',
        adornment,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((option) => ({
        ...option,
        label: (
          <StyledOption color={option.adornment.config?.color}>
            {validateSvg(option.adornment.config?.customIcon) ? (
              <img
                src={option.adornment.config?.customIcon}
                alt={option.adornment.config?.tooltip}
                style={{ maxWidth: '14px', maxHeight: '14px' }}
              />
            ) : (
              <KDSIcon
                icon={`custom-${option.adornment.config?.icon}` as ValidIcons}
              />
            )}
            <StyledOptionLabel>{option.label}</StyledOptionLabel>
          </StyledOption>
        ),
      }));
  }, [adornments]);

  return (
    <StyledIconButtonWrapper>
      <IconButtonAbsoluteMenu
        position="right"
        sizing="dense"
        color={colorsButton.iconGray}
        onChange={handleChooseValue}
        options={menuOptions}
        title={t('Field Actions')}
        restrictCloseHandler
        disabled={disabled}
        data-qa="adornment-menu"
        styles={customStyles}
      >
        <Icon icon="three-dot" />
      </IconButtonAbsoluteMenu>
    </StyledIconButtonWrapper>
  );
};

const SingleAdornment: FC<AdornmentMenuProps> = ({
  adornments,
  disabled,
  onExecuteScript,
}) => {
  const adornment = adornments[0];
  const { triggerProps, tooltipProps, targetProps, showTooltip } =
    useTooltip(true);
  const handleClick = () => {
    if (adornment) {
      onExecuteScript(adornment);
    }
  };

  return adornment ? (
    <StyledAdornmentContainer
      key={adornment.id ?? adornment.plugin_name}
      color={adornment.config?.color}
      data-qa-adornment={adornment.field_type}
    >
      {validateSvg(adornment.config?.customIcon) ? (
        <span {...triggerProps} {...targetProps}>
          <img
            src={adornment.config?.customIcon}
            alt={adornment.config?.tooltip}
            style={{ maxWidth: '14px', maxHeight: '14px' }}
            onClick={handleClick}
          />
          {showTooltip ? (
            <Tooltip
              position="top"
              text={adornment.config?.tooltip}
              modalCompatibility
              {...tooltipProps}
            />
          ) : null}
        </span>
      ) : (
        <Button
          rightIcon={`custom-${adornment.config?.icon}` as ValidIcons}
          variant="text"
          color="inherit"
          rightIconSettings={{
            tooltipDescriptionOverride: adornment?.config?.tooltip,
          }}
          onClick={handleClick}
          disabled={disabled}
        />
      )}
    </StyledAdornmentContainer>
  ) : null;
};

export const IconDataAdornments: FC<IconDataAdornmentsProps> = ({
  adornments,
  scriptArgs,
  disabled,
  executeScript,
  variant,
  translateYValue,
}) => {
  const onExecuteScript = (adornment: DataAdornment) => {
    executeScript(adornment.script, scriptArgs, adornment);
  };

  return (
    <StyledAdornments variant={variant} translateYValue={translateYValue}>
      {adornments.length > 1 ? (
        <AdornmentsMenu
          adornments={adornments}
          onExecuteScript={onExecuteScript}
          disabled={disabled}
        />
      ) : (
        <SingleAdornment
          adornments={adornments}
          onExecuteScript={onExecuteScript}
          disabled={disabled}
        />
      )}
    </StyledAdornments>
  );
};
